import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import {
  AnnouncementService,
  Announcement,
} from '../../services/announcement/announcement.service';
import { DialogService } from '../dialog/dialog.service';
import { DocumentPreviewDialogComponent } from '../../features/help-support/components/document-preview-dialog/document-preview-dialog.component';

interface ViewerDialogData {
  announcements?: Announcement[];
  startIndex?: number;
}

@Component({
  selector: 'app-announcement-viewer-dialog',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, TranslateModule],
  templateUrl: './announcement-viewer-dialog.component.html',
  styleUrls: ['./announcement-viewer-dialog.component.scss'],
})
export class AnnouncementViewerDialogComponent implements OnInit {
  announcements: Announcement[] = [];
  currentIndex = 0;
  isLoading = true;
  isAcknowledging = false;
  acknowledgedCount = 0;

  constructor(
    private announcementService: AnnouncementService,
    private dialogRef: MatDialogRef<AnnouncementViewerDialogComponent>,
    private dialogService: DialogService,
    @Inject(MAT_DIALOG_DATA) private data: ViewerDialogData | null,
  ) {}

  ngOnInit(): void {
    if (this.data?.announcements?.length) {
      this.announcements = this.data.announcements;
      this.currentIndex = this.data.startIndex || 0;
      this.isLoading = false;
    } else {
      this.loadUnreadAnnouncements();
    }
  }

  private loadUnreadAnnouncements(): void {
    this.announcementService.getAll().subscribe({
      next: (data) => {
        this.announcements = data.filter(
          (a) => a.status === 'active' && !a.deletedAt && !a.isRead,
        );
        this.isLoading = false;
        if (this.announcements.length === 0) {
          this.dialogRef.close({ acknowledgedCount: 0 });
        }
      },
      error: () => {
        this.isLoading = false;
        this.dialogRef.close({ acknowledgedCount: 0 });
      },
    });
  }

  get current(): Announcement | null {
    return this.announcements[this.currentIndex] || null;
  }

  get totalCount(): number {
    return this.announcements.length;
  }

  get hasNext(): boolean {
    return this.currentIndex < this.announcements.length - 1;
  }

  acknowledge(): void {
    if (!this.current || this.isAcknowledging) return;

    this.isAcknowledging = true;
    this.announcementService
      .markAsRead(this.current.announcementId)
      .subscribe({
        next: () => {
          this.acknowledgedCount++;
          this.isAcknowledging = false;

          if (this.hasNext) {
            this.currentIndex++;
          } else {
            this.dialogRef.close({
              acknowledgedCount: this.acknowledgedCount,
            });
          }
        },
        error: () => {
          this.isAcknowledging = false;
        },
      });
  }

  navigateNext(): void {
    if (this.hasNext) {
      this.currentIndex++;
    }
  }

  downloadAttachment(): void {
    if (!this.current?.attachmentKey) return;
    const attachmentName = this.current.attachmentName || 'Document.pdf';
    this.announcementService
      .getDownloadUrl(this.current.attachmentKey)
      .subscribe({
        next: (res) => {
          const ref = this.dialogService.openWithComponent({
            component: DocumentPreviewDialogComponent,
            componentData: {
              fileName: attachmentName,
              fileType: this.getFileType(attachmentName),
              downloadUrl: res.downloadUrl,
            },
            hideHeader: true,
            hideActions: true,
            fullContent: true,
            width: '800px',
            maxWidth: '95vw',
            height: '90vh',
            maxHeight: '90vh',
          });
          ref.afterClosed().subscribe((result) => {
            if (result === 'download') {
              this.triggerDownload(res.downloadUrl, attachmentName);
            }
          });
        },
        error: () => {},
      });
  }

  private triggerDownload(url: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private getFileType(fileName: string): string {
    const ext = fileName.split('.').pop()?.toUpperCase() || 'PDF';
    return ext;
  }

  close(): void {
    this.dialogRef.close({ acknowledgedCount: this.acknowledgedCount });
  }
}
